import type {AxiosBasicCredentials} from "axios";

type FormState = { [p: string]: unknown };
type ValueSetter = (name: string, value: any, modelOnly?: boolean) => void;
type KindaName = string|HTMLInputElement|HTMLSelectElement|HTMLTextAreaElement;
type RawValueSetter = (name: KindaName, value: any, event: Event|undefined, modelOnly?: boolean) => void;

export class PortedVueFormController<T extends {[p: string]: any} = {}> {
	public readonly setValue: RawValueSetter;
	public readonly triggerWatches: (event: Event|undefined) => void;

	constructor(
		readonly form: HTMLFormElement,
		readonly formState: FormState&T,
		private readonly watches: { [p: string]: (value: (string|number|boolean|null), valueSetter: ValueSetter, event: Event|undefined) => void } = {}
	) {
		for (const input of form.querySelectorAll<HTMLInputElement|HTMLButtonElement>("input[type='submit'], button:not([type='button'])")) {
			input.addEventListener('click', () => {
				for (const input of form.querySelectorAll("input,select,textarea")) {
					input.classList.toggle("has-been-entered", true);
				}
			});
		}
		this.setValue = (name, value, event: Event|undefined, modelOnly = false) => {
			if (typeof name !== 'string') {
				name = name.name;
			}
			const input = form.elements.namedItem(name);
			if (!input) {
				return;
			}
			let usedName: string;
			if (input instanceof RadioNodeList) {
				if (!modelOnly) {
					input.value = value;
					if (input.value !== value) { // Then "value" isn't in the radio list. So, we uncheck everything
						for (const item of input) {
							if (item instanceof HTMLInputElement) {
								item.checked = false;
							}
						}
					}
				}
				usedName = this.handleUpdatingJSFormStateValue(name, input.item(0), value);
			} else if (input instanceof HTMLInputElement) {
				const isToggle = input.type === 'checkbox' && input.getAttribute("data-model-type") === "toggle";
				if (isToggle) {
					value = value === 'true' || (typeof value !== 'string' && !!value);
					if (!modelOnly) {
						input.checked = value;
					}
					usedName = this.handleUpdatingJSFormStateValue(name, input, value);
				} else {
					if (!modelOnly) {
						input.value = value;
					}
					usedName = this.handleUpdatingJSFormStateValue(name, input, value);
				}
			} else if (input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement) {
				if (!modelOnly) {
					input.value = value;
				}
				usedName = this.handleUpdatingJSFormStateValue(name, input, value);
			} else {
				return;
			}
			watches[usedName]?.(this.getFormStateValue(usedName), (name, value, modelOnly) => this.setValue(name, value, event, modelOnly), event);
		};
		this.triggerWatches = (event: Event|undefined) => {
			for (const [key, watch] of Object.entries(watches)) {
				watch((this.getFormStateValue(key)), (name, value, modelOnly) => this.setValue(name, value, event, modelOnly), event);
			}
		};

		for (const input of this.modeledInputsIn()) {
			this.wireUpBasicInput(input, this.setValue);
		}
	}

	private wireUpBasicInput(input: HTMLInputElement|HTMLTextAreaElement|HTMLSelectElement, valueSetter: RawValueSetter) {
		const name = input.name;
		if (name) {
			const inputName = input.getAttribute("data-model") ?? name;
			const defaultInput = this.getFormStateValue(inputName)?.toString();
			if (defaultInput) {
				input.value = defaultInput;
			} else {
				this.setFormStateValue(inputName, this.getInputValue(name));
			}
			const onUpdate = (event: Event) => {
				if (event.type !== 'input') {
					input.classList.toggle("has-been-entered", true);
				}
				const usedName = this.handleUpdatingJSFormStateValue(name, input, this.getInputValue(name));
				this.watches[usedName]?.(this.getFormStateValue(usedName), (name, value, modelOnly) => valueSetter(name, value, event, modelOnly), event);
			};
			input.addEventListener('change', onUpdate);
			input.addEventListener('input', onUpdate);
			input.addEventListener('focusout', () => input.classList.toggle("has-been-entered", true));
			if (input instanceof HTMLInputElement && (input.type === "checkbox" || input.type === "radio")) {
				input.addEventListener('click', onUpdate);
			}
			if (input.required && !input.classList.contains("has-own-validation")) {
				const name = (input.getAttribute("data-name-for-messages")?.trim() ?? input.labels?.item(0)?.innerText ?? input.ariaLabel)?.trim();
				if (name) {
					const target = input.type === 'radio' ? input.closest("fieldset") : input.parentElement;
					if (target && !target.querySelector(".gfield_validation_message")) {
						const message = document.createElement("div");
						message.classList.add("gfield_description", "validation_message", "gfield_validation_message");
						message.innerText = `"${name}" is required`;
						input.after(message);
					}
				}
			}
			input.addEventListener('invalid', () => input.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"}));
		}
	}

	private handleUpdatingJSFormStateValue(name: string, input: Node|null, value: any): string {
		if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement || input instanceof HTMLTextAreaElement) {
			const usedName = input.getAttribute("data-model") ?? name;
			this.setFormStateValue(usedName, input.getAttribute("data-model-requires-valid") !== 'true' || input.checkValidity() ? (value ?? null) : null);
			return usedName;
		} else {
			this.setFormStateValue(name, value ?? null);
		}
		return name;
	}

	public setFormStateValue(modelName: string, value: unknown) {
		let current: FormState = this.formState;
		const parts = modelName.split(".");
		for (let i = 0; i < parts.length - 1; i++) {
			if (!(parts[i] in current)) {
				current[parts[i]] = {};
			}
			current = current[parts[i]] as FormState;
		}
		current[parts[parts.length - 1]] = value;
	}

	public getFormStateValue(modelName: string): string|number|boolean|null {
		let current: FormState = this.formState;
		const parts = modelName.split(".");
		for (let i = 0; i < parts.length - 1; i++) {
			if (!(parts[i] in current)) {
				return null;
			}
			current = current[parts[i]] as FormState;
		}
		const res = current[parts[parts.length - 1]];
		if (typeof res === 'string' || typeof res === 'number' || typeof res === 'boolean' || res === null) {
			return res;
		}
		return null;
	}

	private *modeledInputsIn() {
		for (const input of this.form.elements) {
			if (input instanceof RadioNodeList) {
				for (const inp of input) {
					if (inp instanceof HTMLInputElement && inp.name) {
						yield inp;
					}
				}
			} else if (input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement) {
				if (input.name) {
					yield input;
				}
			} else if (input instanceof HTMLInputElement) {
				if (input.name && input.type !== 'button' && input.type !== 'submit') {
					yield input;
				}
			}
		}
	}
	getInputValue(name: string) {
		const input = this.form.elements.namedItem(name);
		if (input instanceof RadioNodeList || input instanceof HTMLTextAreaElement || input instanceof HTMLSelectElement) {
			return input.value || null;
		} else if (input instanceof HTMLInputElement) {
			if (input.type === 'checkbox' && input.getAttribute("data-model-type") === "toggle") {
				return input.checked;
			}
			return input.value || null;
		}
		return null;
	}
}

declare global {
	interface Window {
		axiosAuth?: AxiosBasicCredentials
	}
}
